import React, { useState, useMemo } from 'react';
import { Box, Modal, Button, CircularProgress } from '@mui/material';
import { InputNumber } from '@fashionnovateam/nova-ui-toolkit';
import { FeatureFlagsContext } from '../../../../Lib/Contexts/FeatureFlagsContext';

import CloseButton from '../Buttons/CloseButton';
import Switch from '../../Switch';
import { ReactComponent as PrintIcon } from '../../../../Assets/icons/printIcon.svg';
import styles from './PrintLabelModal.module.css';
import TextInput from '../../TextInput/TextInput';
import {
  generatePoLineUnitLabelZPL,
  generatePoUnitLabelZPL,
} from '../../../../Lib/Utils/Asn/ZPLFileCreation/generate-unit-label';
import {
  generatePoHangtagZPL,
  generatePoLineHangtagZPL,
} from '../../../../Lib/Utils/Asn/ZPLFileCreation/generate-hangtag';
import { generatePoLineCartonZPL } from '../../../../Lib/Utils/Asn/ZPLFileCreation/generate-carton';
import {
  useBrowserPrint,
  PrinterStatus,
} from '../../../../Lib/Hooks/browserPrint.hooks';
import { useUpdatePrintRequestStatus } from '../../../../Lib/Hooks/printRequestStatus.hooks';
import { PrintRequestStatus } from '../../../../Lib/Api/asn';
import {
  PurchaseOrder,
  PurchaseOrderLine,
} from '../../../../Lib/Types/purchase-order';
import toast from '../../../Toast/Toast';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  poInformation?: PurchaseOrder;
  lineInformation?: PurchaseOrderLine;
  userId: string;
}

interface FormProps {
  value: string;
  handleUpdate: () => void;
}

interface PropsBaseModal {
  open: boolean;
  printerStatus: 'loading' | PrinterStatus;
  onClose: () => void;
  onConfirm: (value: number) => void;
  checkThreshold: (value: number, totalLabel: number) => boolean;
  poInformation?: PurchaseOrderLine | PurchaseOrder;
  title: string;
  warningText: string;
  totalLabel: number;
  PoInfo: any;
  Form: any;
}

interface PropsCartonModal {
  open: boolean;
  printerStatus: 'loading' | PrinterStatus;
  onClose: () => void;
  onConfirm: (unitCount: number, labelCount: number, isMix: boolean) => void;
  poInformation: PurchaseOrderLine | PurchaseOrder;
  Form: any;
  merchType: string;
}

const getWarningText = (poLevel: boolean) =>
  poLevel
    ? 'The additional printing value is greater than PO quantity. Submit a second time to confirm.'
    : 'The printing value is greater than PO quantity. Submit a second time to confirm.';

const handlePrintingThreshold = (poLevel: boolean) => {
  if (poLevel) {
    return (value: number, totalLabel: number) =>
      (value / 100 + 1) * totalLabel > totalLabel * 2;
  }
  return (value: number, totalLabel: number) => value > totalLabel * 2;
};

const calcPoLevelQtd = (poInformation: PurchaseOrder) =>
  poInformation.purchase_order_lines.reduce(
    (labelQuantity, item) => labelQuantity + (item?.quantity || 0),
    0,
  );

function PoLevelInfo({
  poInformation,
  totalLabel,
}: {
  poInformation: PurchaseOrder;
  totalLabel?: number;
}) {
  return (
    <div className={styles.poInfo}>
      <div className={styles.poInfoSummary}>
        <span>{poInformation.po_number}</span>
        {totalLabel && (
          <>
            <span>•</span>
            <span className={styles.labelCount}>{totalLabel} total label</span>
          </>
        )}
      </div>
    </div>
  );
}

function ItemLevelInfo({
  poInformation,
  totalLabel,
}: {
  poInformation: PurchaseOrderLine;
  totalLabel?: number;
}) {
  const lastDash = poInformation.item_name?.lastIndexOf(' - ') ?? -1;
  const productName =
    lastDash > 0
      ? poInformation.item_name.slice(0, lastDash)
      : poInformation.item_name;
  const color =
    lastDash > 0 ? poInformation.item_name.slice(lastDash + 3) : null;

  return (
    <div className={styles.poInfo}>
      <div className={styles.poInfoSummary}>
        <span>{poInformation.po_number}</span>
        {color && (
          <>
            <span>-</span>
            <span>{color}</span>
          </>
        )}
        {totalLabel && (
          <>
            <span>•</span>
            <span className={styles.labelCount}>{totalLabel} total label</span>
          </>
        )}
      </div>
      <div className={styles.itemName}>{productName}</div>
    </div>
  );
}

function PoLevelForm({ value, handleUpdate }: FormProps) {
  return (
    <div className={styles.modalForm}>
      Print an additional
      <TextInput
        className={styles.textLabel}
        outlined
        autoFocus
        name="label %"
        margin="dense"
        value={value}
        onChange={handleUpdate}
      />
      % of labels
    </div>
  );
}

function ItemLevelForm({ value, handleUpdate }: FormProps) {
  return (
    <div className={styles.modalForm}>
      Print
      <TextInput
        className={styles.textLabel}
        outlined
        autoFocus
        name="label"
        margin="dense"
        value={value}
        onChange={handleUpdate}
      />
      labels
    </div>
  );
}

function PrintLabelModal({
  open,
  printerStatus,
  onClose,
  onConfirm,
  checkThreshold,
  title,
  warningText,
  poInformation,
  totalLabel = 0,
  PoInfo,
  Form,
}: PropsBaseModal) {
  const [value, setValue] = useState('');
  const [warning, setWarning] = useState(false);

  const validate = () => !isNaN(+value);
  const isValid = useMemo(() => validate(), [value]);

  const handleSubmit = async () => {
    if (!warning && checkThreshold(+value, totalLabel)) {
      setWarning(true);
    } else {
      onConfirm(+value);
    }
  };

  const handleUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  return (
    <Modal open={open} onClose={onClose} className={styles.modal}>
      <Box className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <CloseButton onClick={onClose} size={14} />
        </div>

        <Box className={styles.centralModal}>
          <div>
            <div className={styles.modalTitle}> {title} </div>
            <PoInfo poInformation={poInformation} totalLabel={totalLabel} />
          </div>

          <Form value={value} handleUpdate={handleUpdate} />
          {warning && <div style={{ color: 'red' }}>{warningText}</div>}
          {printerStatus === 'offline' ? (
            <div style={{ color: 'red' }}>Your printer is not connected.</div>
          ) : null}
          {printerStatus !== 'loading' &&
          printerStatus !== 'ready' &&
          printerStatus !== 'offline' ? (
            <div style={{ color: 'red' }}>
              Your printer is not correctly configured or not ready to print.
            </div>
          ) : null}
        </Box>

        <div className={styles.buttonContainer}>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || printerStatus !== 'ready'}
          >
            {printerStatus === 'loading' ? (
              <>
                <CircularProgress size={20} /> Loading printer
              </>
            ) : (
              <>
                <PrintIcon /> Print
              </>
            )}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </Box>
    </Modal>
  );
}

function PrintCartonLabelModal({
  open,
  printerStatus,
  onClose,
  onConfirm,
  poInformation,
  merchType,
}: PropsCartonModal) {
  const { flags } = React.useContext(FeatureFlagsContext);
  const allowEmptyUnitCount =
    flags.allow_empty_carton_unit_count &&
    flags.allow_empty_carton_unit_count.enabled;

  const [values, setValues] = useState({
    unitCount: -1,
    labelCount: -1,
  });
  const [errors, setErrors] = useState({
    unitCount: false,
    labelCount: false,
  });
  const [isMix, setIsMix] = useState(false);
  const isValid = useMemo(() => {
    if (allowEmptyUnitCount) {
      return values.labelCount > 0;
    }
    return Object.values(values).every((value) => value > 0);
  }, [values]);

  const handleSubmit = async () => {
    onConfirm(values.unitCount, values.labelCount, isMix);
  };

  const handleErrors = (fieldName: string, field?: number) =>
    setErrors({ ...errors, [fieldName]: !field });

  const handleSwitchChange = (value: boolean) => setIsMix(value);

  const handleUpdate = (valueName: string, value?: number) =>
    setValues({ ...values, [valueName]: value });

  return (
    <Modal open={open} onClose={onClose} className={styles.modal}>
      <Box className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <CloseButton onClick={onClose} size={14} />
        </div>

        <Box className={styles.centralModal}>
          <div>
            <div className={styles.modalTitle}> PRINT CARTON LABELS </div>
            {isMix ? (
              <PoLevelInfo poInformation={poInformation as PurchaseOrder} />
            ) : (
              <ItemLevelInfo
                poInformation={poInformation as PurchaseOrderLine}
              />
            )}
          </div>

          {merchType.toLowerCase() === 'direct' && (
            <div>
              <Switch
                sx={{ m: 1 }}
                onChange={(_: any, value: boolean) => handleSwitchChange(value)}
              />
              <span>This is a mixed carton</span>
            </div>
          )}

          <div className="w-full">
            <div className="pb-3">
              <span className="text-black/80 font-semibold">Unit Count</span>
              {allowEmptyUnitCount ? null : (
                <span className="text-danger-700 pl-[2px]">*</span>
              )}
            </div>
            <InputNumber
              className="w-full h-11"
              name="unitCount"
              required={!allowEmptyUnitCount}
              onChangeForm={handleUpdate}
              onBlurForm={handleErrors}
              error={allowEmptyUnitCount ? false : errors.unitCount}
            />
          </div>
          <div className="w-full">
            <div className="pb-3">
              <span className="text-black/80 font-semibold">
                Number of labels to print
              </span>
              <span className="text-danger-700 pl-[2px]">*</span>
            </div>
            <InputNumber
              className="w-full h-11"
              name="labelCount"
              required
              onChangeForm={handleUpdate}
              onBlurForm={handleErrors}
              error={errors.labelCount}
            />
          </div>
          {printerStatus === 'offline' ? (
            <div style={{ color: 'red' }}>Your printer is not connected.</div>
          ) : null}
          {printerStatus !== 'loading' &&
          printerStatus !== 'ready' &&
          printerStatus !== 'offline' ? (
            <div style={{ color: 'red' }}>
              Your printer is not correctly configured or not ready to print.
            </div>
          ) : null}
        </Box>

        <div className={styles.buttonContainer}>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || printerStatus !== 'ready'}
          >
            {printerStatus === 'loading' ? (
              <>
                <CircularProgress size={20} /> Loading printer
              </>
            ) : (
              <>
                <PrintIcon /> Print
              </>
            )}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </Box>
    </Modal>
  );
}

function UnitLabelModal({
  open,
  onClose,
  onConfirm,
  poInformation,
  lineInformation,
  userId,
}: Props) {
  const {
    selectedDevices,
    loading: isBrowserPrintLoading,
    sendZplCmd,
  } = useBrowserPrint();
  const [isLoading, setIsLoading] = useState(false);

  if (!open || (!poInformation && !lineInformation)) return null;

  let totalLabel = 0;
  if (poInformation) totalLabel = calcPoLevelQtd(poInformation);
  else if (lineInformation) totalLabel = lineInformation.quantity;

  const generateAndPrintUnitLabel = async (value: number) => {
    setIsLoading(true);
    if (poInformation) {
      const labelsArray = await generatePoUnitLabelZPL(
        userId,
        poInformation.po_number,
        value,
      );
      if (!labelsArray || !labelsArray.length) {
        setIsLoading(false);
        return;
      }
      const errors = [];
      let successCount = 0;

      for await (const label of labelsArray) {
        const zplPreview = label.zpl[0]?.substring(0, 100) || 'No ZPL data';
        try {
          await sendZplCmd(
            userId,
            'unitLabel',
            label.zpl.join(' '),
            label.request_id,
          );
          successCount += 1;
        } catch (error) {
          console.error(`Failed to print label (${zplPreview}...):`, error);
          errors.push({
            requestId: label.request_id,
            zplPreview,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      if (errors.length > 0) {
        console.error(`Failed to print ${errors.length} labels:`, errors);
        toast(
          {
            title: `Printing completed with ${errors.length} failed labels`,
            description: `Successfully printed ${successCount} of ${labelsArray.length} labels.`,
          },
          { type: 'warning' },
        );
      }
    } else if (lineInformation) {
      const labels = await generatePoLineUnitLabelZPL(
        userId,
        lineInformation.id,
        value,
      );
      if (!labels) {
        setIsLoading(false);
        return;
      }
      console.log('Unit Label ZPL:', labels);
      try {
        await sendZplCmd(
          userId,
          'unitLabel',
          labels?.zpl.join(' '),
          labels?.request_id,
        );
      } catch (error) {
        console.error('Failed to print line item label:', error);
        toast(
          {
            title: 'Print failed',
            description: 'Failed to print label. Please try again.',
          },
          { type: 'error' },
        );
      }
    }
    onConfirm();
    setIsLoading(false);
  };

  return (
    <PrintLabelModal
      open={open}
      printerStatus={
        isLoading || isBrowserPrintLoading
          ? 'loading'
          : selectedDevices.unitLabel.status
      }
      onClose={onClose}
      onConfirm={generateAndPrintUnitLabel}
      poInformation={poInformation || lineInformation}
      totalLabel={totalLabel}
      checkThreshold={handlePrintingThreshold(!!poInformation)}
      title="PRINT UNIT LABEL"
      warningText={getWarningText(!!poInformation)}
      Form={poInformation ? PoLevelForm : ItemLevelForm}
      PoInfo={poInformation ? PoLevelInfo : ItemLevelInfo}
    />
  );
}

function HangtagModal({
  open,
  onClose,
  onConfirm,
  poInformation,
  lineInformation,
  userId,
}: Props) {
  const {
    selectedDevices,
    loading: isBrowserPrintLoading,
    sendZplCmd,
  } = useBrowserPrint();
  const updatePrintRequestStatus = useUpdatePrintRequestStatus();
  const [isLoading, setIsLoading] = useState(false);

  if (!open || (!poInformation && !lineInformation)) return null;

  let totalLabel = 0;
  if (poInformation) totalLabel = calcPoLevelQtd(poInformation);
  else if (lineInformation) totalLabel = lineInformation.quantity;

  const generateHangtag = async (value: number) => {
    setIsLoading(true);
    if (poInformation) {
      const labelsArray = await generatePoHangtagZPL(
        userId,
        poInformation.po_number,
        value,
      );
      if (!labelsArray || !labelsArray.length) {
        setIsLoading(false);
        return;
      }
      const errors = [];
      let successCount = 0;

      for (const label of labelsArray) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await sendZplCmd(
            userId,
            'hangTag',
            label.zpl.join(' '),
            label.request_id,
          );
          successCount += 1;
        } catch (error) {
          console.error('Failed to print hangtag:', error);
          errors.push({
            requestId: label.request_id,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      if (errors.length > 0) {
        console.error(`Failed to print ${errors.length} hangtags:`, errors);
        toast(
          {
            title: `Hangtag printing completed with ${errors.length} failed labels`,
            description: `Successfully printed ${successCount} of ${labelsArray.length} hangtags. Check console for error details.`,
          },
          { type: 'warning' },
        );
      }
    } else if (lineInformation) {
      const labels = await generatePoLineHangtagZPL(
        userId,
        lineInformation.id,
        value,
      );
      if (!labels) {
        setIsLoading(false);
        return;
      }
      updatePrintRequestStatus.mutate({
        userId,
        requestId: labels.request_id,
        status: PrintRequestStatus.ZPL_GENERATED,
      });
      console.log('Hangtag Label ZPL:', labels);
      try {
        await sendZplCmd(
          userId,
          'hangTag',
          labels?.zpl.join(' '),
          labels?.request_id,
        );
      } catch (error) {
        console.error('Failed to print hangtag:', error);
        toast(
          {
            title: 'Hangtag print failed',
            description: 'Failed to print hangtag. Please try again.',
          },
          { type: 'error' },
        );
      }
    }
    onConfirm();
    setIsLoading(false);
  };

  return (
    <PrintLabelModal
      open={open}
      printerStatus={
        isLoading || isBrowserPrintLoading
          ? 'loading'
          : selectedDevices.hangTag.status
      }
      onClose={onClose}
      onConfirm={generateHangtag}
      poInformation={poInformation || lineInformation}
      totalLabel={totalLabel}
      checkThreshold={handlePrintingThreshold(!!poInformation)}
      title="PRINT HANGTAG"
      warningText={getWarningText(!!poInformation)}
      Form={poInformation ? PoLevelForm : ItemLevelForm}
      PoInfo={poInformation ? PoLevelInfo : ItemLevelInfo}
    />
  );
}

function CartonLabelModal({
  open,
  onClose,
  onConfirm,
  lineInformation,
  userId,
  merchType,
}: Props & { merchType: string }) {
  const {
    selectedDevices,
    loading: isBrowserPrintLoading,
    sendZplCmd,
  } = useBrowserPrint();
  const updatePrintRequestStatus = useUpdatePrintRequestStatus();
  const [isLoading, setIsLoading] = useState(false);

  if (!open || !lineInformation) return null;

  const generateAndPrintCartonLabel = async (
    unitCount: number,
    labelCount: number,
    isMix: boolean,
  ) => {
    setIsLoading(true);
    const labels = await generatePoLineCartonZPL(
      userId,
      lineInformation.id,
      unitCount,
      labelCount,
      isMix,
    );
    if (!labels) {
      setIsLoading(false);
      return;
    }
    updatePrintRequestStatus.mutate({
      userId,
      requestId: labels.request_id,
      status: PrintRequestStatus.ZPL_GENERATED,
    });
    console.log('Carton Label ZPL:', labels);
    try {
      await sendZplCmd(
        userId,
        'cartonLabel',
        labels?.zpl.join(' '),
        labels?.request_id,
      );
    } catch (error) {
      console.error('Failed to print carton label:', error);
      toast(
        {
          title: 'Carton label print failed',
          description: 'Failed to print carton label. Please try again.',
        },
        { type: 'error' },
      );
    }
    onConfirm();
    setIsLoading(false);
  };

  return (
    <PrintCartonLabelModal
      open={open}
      printerStatus={
        isLoading || isBrowserPrintLoading
          ? 'loading'
          : selectedDevices.cartonLabel.status
      }
      onClose={onClose}
      onConfirm={generateAndPrintCartonLabel}
      poInformation={lineInformation}
      Form={ItemLevelForm}
      merchType={merchType}
    />
  );
}

export { UnitLabelModal, HangtagModal, CartonLabelModal };
